import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection, addDoc, getDocs, query, orderBy, serverTimestamp, 
  where, onSnapshot, updateDoc, doc, deleteDoc, getDoc, setDoc
} from 'firebase/firestore';

function DirectMessage() {
  const [localUser, setLocalUser] = useState(null);
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // 사용자 정보 로드
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        console.log('사용자 정보 로드됨:', user);
        setLocalUser(user);
      } else {
        console.log('저장된 사용자 정보 없음');
        setLocalUser(null);
      }
    } catch (error) {
      console.error('사용자 정보 로드 실패:', error);
      setLocalUser(null);
    }
  }, []);

  // 메시지 자동 스크롤
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (localUser) {
      fetchFriends();
      fetchFriendRequests();
    }
  }, [localUser]);

  useEffect(() => {
    let unsubscribe;
    if (localUser && selectedFriend) {
      const chatId = [localUser.id, selectedFriend.id].sort().join('_');
      const q = query(
        collection(db, 'direct_messages'),
        where('chatId', '==', chatId),
        orderBy('createdAt', 'asc')
      );
      
      unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const messagesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setMessages(messagesData);
        } catch (error) {
          console.error('메시지 데이터 처리 오류:', error);
        }
      }, (error) => {
        console.error('메시지 리스너 오류:', error);
        // 오류 발생 시 리스너 재연결 시도
        if (error.code === 'unavailable') {
          console.log('Firestore 연결 재시도 중...');
        }
      });
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [localUser, selectedFriend]);

  // 사용자가 로그인하지 않은 경우 처리
  if (!localUser) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          로그인이 필요합니다.
        </div>
      </div>
    );
  }

  // 친구 목록 가져오기
  const fetchFriends = async () => {
    try {
      const q = query(
        collection(db, 'friendships'),
        where('users', 'array-contains', localUser.id),
        where('status', '==', 'accepted')
      );
      const snapshot = await getDocs(q);
      const friendsData = [];
      
      for (const docSnapshot of snapshot.docs) {
        const friendship = docSnapshot.data();
        
        // users 배열이 존재하고 유효한지 확인
        if (!friendship.users || !Array.isArray(friendship.users) || friendship.users.length < 2) {
          console.warn('유효하지 않은 friendship 데이터:', friendship);
          continue;
        }
        
        const friendId = friendship.users.find(id => id !== localUser.id);
        
        // friendId가 유효한지 확인
        if (!friendId) {
          console.warn('친구 ID를 찾을 수 없음:', friendship);
          continue;
        }
        
        // 친구 정보 가져오기
        const userDoc = await getDoc(doc(db, 'users', friendId));
        if (userDoc.exists()) {
          // 읽지 않은 메시지 수 가져오기
          const chatId = [localUser.id, friendId].sort().join('_');
          const unreadQuery = query(
            collection(db, 'direct_messages'),
            where('chatId', '==', chatId),
            where('receiverId', '==', localUser.id),
            where('read', '==', false)
          );
          const unreadSnapshot = await getDocs(unreadQuery);
          const unreadCount = unreadSnapshot.size;
          
          friendsData.push({
            id: friendId,
            ...userDoc.data(),
            friendshipId: docSnapshot.id,
            lastMessage: friendship.lastMessage || null,
            lastMessageTime: friendship.lastMessageTime || null,
            unreadCount
          });
        }
      }
      
      // 마지막 메시지 시간순으로 정렬
      friendsData.sort((a, b) => {
        if (!a.lastMessageTime && !b.lastMessageTime) return 0;
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return b.lastMessageTime.toDate() - a.lastMessageTime.toDate();
      });
      
      setFriends(friendsData);
    } catch (error) {
      console.error('친구 목록 로드 실패:', error);
    }
  };

  // 친구 요청 가져오기
  const fetchFriendRequests = async () => {
    try {
      // 받은 요청
      const receivedQuery = query(
        collection(db, 'friend_requests'),
        where('to', '==', localUser.id),
        where('status', '==', 'pending')
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedRequests = [];
      
      for (const docSnapshot of receivedSnapshot.docs) {
        const request = docSnapshot.data();
        const userDoc = await getDoc(doc(db, 'users', request.from));
        if (userDoc.exists()) {
          receivedRequests.push({
            id: docSnapshot.id,
            ...request,
            fromUser: userDoc.data()
          });
        }
      }
      
      // 보낸 요청
      const sentQuery = query(
        collection(db, 'friend_requests'),
        where('from', '==', localUser.id),
        where('status', '==', 'pending')
      );
      const sentSnapshot = await getDocs(sentQuery);
      const sentRequests = sentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFriendRequests(receivedRequests);
      setSentRequests(sentRequests);
    } catch (error) {
      console.error('친구 요청 로드 실패:', error);
    }
  };

  // 사용자 검색
  const searchUsers = async () => {
    if (!searchUser.trim()) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const q = query(collection(db, 'users'));
      const snapshot = await getDocs(q);
      const users = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => 
          user.id !== localUser.id &&
          (user.nickname?.toLowerCase().includes(searchUser.toLowerCase()) ||
           user.name?.toLowerCase().includes(searchUser.toLowerCase()))
        );
      
      setSearchResults(users);
    } catch (error) {
      console.error('사용자 검색 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 친구 요청 보내기
  const sendFriendRequest = async (toUserId) => {
    try {
      // 이미 친구인지 확인
      const friendshipQuery = query(
        collection(db, 'friendships'),
        where('users', 'array-contains', localUser.id)
      );
      const friendshipSnapshot = await getDocs(friendshipQuery);
      const isFriend = friendshipSnapshot.docs.some(doc => 
        doc.data().users.includes(toUserId)
      );
      
      if (isFriend) {
        alert('이미 친구입니다.');
        return;
      }
      
      // 이미 요청을 보냈는지 확인
      const requestQuery = query(
        collection(db, 'friend_requests'),
        where('from', '==', localUser.id),
        where('to', '==', toUserId),
        where('status', '==', 'pending')
      );
      const requestSnapshot = await getDocs(requestQuery);
      
      if (!requestSnapshot.empty) {
        alert('이미 친구 요청을 보냈습니다.');
        return;
      }
      
      await addDoc(collection(db, 'friend_requests'), {
        from: localUser.id,
        to: toUserId,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      
      // 알림 생성
      await addDoc(collection(db, 'notifications'), {
        from: localUser.id,
        to: toUserId,
        type: 'friend_request',
        createdAt: serverTimestamp(),
        read: false
      });
      
      alert('친구 요청을 보냈습니다.');
      fetchFriendRequests();
    } catch (error) {
      console.error('친구 요청 실패:', error);
      alert('친구 요청에 실패했습니다.');
    }
  };

  // 친구 요청 수락
  const acceptFriendRequest = async (requestId, fromUserId) => {
    try {
      // 친구 관계 생성
      await addDoc(collection(db, 'friendships'), {
        users: [localUser.id, fromUserId],
        status: 'accepted',
        createdAt: serverTimestamp()
      });
      
      // 친구 요청 상태 업데이트
      await updateDoc(doc(db, 'friend_requests', requestId), {
        status: 'accepted'
      });
      
      // 친구 요청을 보낸 사용자에게 수락 알림 생성
      await addDoc(collection(db, 'notifications'), {
        from: localUser.id,
        to: fromUserId,
        type: 'friend_request_accepted',
        message: `${localUser.name || localUser.nickname || '사용자'}님이 친구 요청을 수락했습니다.`,
        createdAt: serverTimestamp(),
        read: false
      });
      
      alert('친구 요청을 수락했습니다.');
      fetchFriends();
      fetchFriendRequests();
    } catch (error) {
      console.error('친구 요청 수락 실패:', error);
      alert('친구 요청 수락에 실패했습니다.');
    }
  };

  // 친구 요청 거절
  const rejectFriendRequest = async (requestId, fromUserId) => {
    try {
      await updateDoc(doc(db, 'friend_requests', requestId), {
        status: 'rejected'
      });
      
      // 친구 요청을 보낸 사용자에게 거절 알림 생성
      await addDoc(collection(db, 'notifications'), {
        from: localUser.id,
        to: fromUserId,
        type: 'friend_request_rejected',
        message: `${localUser.name || localUser.nickname || '사용자'}님이 친구 요청을 거절했습니다.`,
        createdAt: serverTimestamp(),
        read: false
      });
      
      alert('친구 요청을 거절했습니다.');
      fetchFriendRequests();
    } catch (error) {
      console.error('친구 요청 거절 실패:', error);
    }
  };



  // 메시지 보내기
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedFriend || !localUser) {
      console.log('메시지 전송 조건 확인:', {
        hasMessage: !!newMessage.trim(),
        hasSelectedFriend: !!selectedFriend,
        hasLocalUser: !!localUser
      });
      return;
    }
    
    try {
      console.log('메시지 전송 시작:', {
        senderId: localUser.id,
        receiverId: selectedFriend.id,
        content: newMessage
      });
      
      const chatId = [localUser.id, selectedFriend.id].sort().join('_');
      
      await addDoc(collection(db, 'direct_messages'), {
        chatId,
        senderId: localUser.id,
        receiverId: selectedFriend.id,
        content: newMessage,
        createdAt: serverTimestamp(),
        read: false
      });
      
      console.log('메시지 전송 성공');
      
      // 친구 관계에 마지막 메시지 업데이트 (friendshipId가 있는 경우에만)
      if (selectedFriend.friendshipId) {
        try {
          const friendshipRef = doc(db, 'friendships', selectedFriend.friendshipId);
          await updateDoc(friendshipRef, {
            lastMessage: newMessage,
            lastMessageTime: serverTimestamp()
          });
        } catch (error) {
          console.warn('친구 관계 업데이트 실패:', error);
        }
      }
      
      setNewMessage('');
      // 친구 목록 업데이트 (읽지 않은 메시지 수 포함)
      fetchFriends();
    } catch (error) {
      console.error('메시지 전송 실패:', error);
      alert('메시지 전송에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 메시지 읽음 처리
  const markMessageAsRead = async (messageId) => {
    try {
      await updateDoc(doc(db, 'direct_messages', messageId), {
        read: true,
        readAt: serverTimestamp()
      });
      // 친구 목록 업데이트 (읽지 않은 메시지 수 갱신)
      fetchFriends();
    } catch (error) {
      console.error('메시지 읽음 처리 실패:', error);
    }
  };

  // 메시지 삭제
  const deleteMessage = async (messageId) => {
    if (!window.confirm('정말로 이 메시지를 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'direct_messages', messageId));
      console.log('메시지 삭제 성공');
      // 친구 목록 업데이트 (읽지 않은 메시지 수 갱신)
      fetchFriends();
    } catch (error) {
      console.error('메시지 삭제 실패:', error);
      alert('메시지 삭제에 실패했습니다.');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ko-KR', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  // 채팅방이 선택된 경우
  if (selectedFriend && localUser) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '600px',
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* 채팅방 헤더 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff'
        }}>
          <button
            onClick={() => setSelectedFriend(null)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            ← 뒤로
          </button>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 700
          }}>
            {(selectedFriend.name || selectedFriend.nickname || '?')[0]}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>
              {selectedFriend.name || selectedFriend.nickname || '알 수 없음'}
            </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              {selectedFriend.tier || '루키'} • MMR {selectedFriend.mmr || 1000}
            </div>
          </div>
        </div>

        {/* 메시지 목록 */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: 16,
          background: '#f8fafc'
        }}>
          {messages.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: '#64748b',
              marginTop: 40
            }}>
              첫 메시지를 보내보세요! 💬
            </div>
          ) : (
            messages.map(message => {
              const isMyMessage = message.senderId === localUser.id;
              const isRead = message.read;
              const canDelete = isMyMessage && !isRead;
              
              return (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                    marginBottom: 12,
                    position: 'relative'
                  }}
                  onMouseEnter={() => {
                    // 상대방 메시지가 읽지 않았으면 읽음 처리
                    if (!isMyMessage && !isRead) {
                      markMessageAsRead(message.id);
                    }
                  }}
                >
                  <div style={{
                    maxWidth: '70%',
                    padding: '12px 16px',
                    borderRadius: 16,
                    background: isMyMessage 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : '#fff',
                    color: isMyMessage ? '#fff' : '#374151',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    position: 'relative',
                    border: !isRead && !isMyMessage ? '2px solid #fbbf24' : 'none'
                  }}>
                    <div style={{ fontSize: 14, lineHeight: 1.4, marginBottom: 4 }}>
                      {message.content}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 11,
                      opacity: 0.7
                    }}>
                      <span>{formatTime(message.createdAt)}</span>
                      {isMyMessage && (
                        <span style={{ marginLeft: 8 }}>
                          {isRead ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                    {message.readAt && (
                      <div style={{
                        fontSize: 10,
                        opacity: 0.6,
                        marginTop: 2
                      }}>
                        읽음: {formatTime(message.readAt)}
                      </div>
                    )}
                  </div>
                  
                  {/* 삭제 버튼 (내 메시지이고 읽지 않았을 때만) */}
                  {canDelete && (
                    <button
                      onClick={() => deleteMessage(message.id)}
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: isMyMessage ? -8 : 'auto',
                        left: isMyMessage ? 'auto' : -8,
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        fontSize: 10,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = 1}
                      onMouseLeave={(e) => e.target.style.opacity = 0}
                      title="메시지 삭제"
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 메시지 입력 */}
        <div style={{
          display: 'flex',
          gap: 12,
          padding: 16,
          background: '#fff',
          borderTop: '1px solid #f1f5f9'
        }}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="메시지를 입력하세요..."
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              fontSize: 14,
              outline: 'none'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                console.log('Enter 키 눌림, 메시지 전송 시도');
                sendMessage();
              }
            }}
          />
          <button
            onClick={() => {
              console.log('전송 버튼 클릭, 메시지 전송 시도');
              sendMessage();
            }}
            disabled={!localUser || !selectedFriend}
            style={{
              background: (!localUser || !selectedFriend) 
                ? '#cbd5e1' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 600,
              cursor: (!localUser || !selectedFriend) ? 'not-allowed' : 'pointer'
            }}
          >
            전송
          </button>
        </div>
      </div>
    );
  }

  // 메인 화면
  return (
    <div>
      {/* 탭 메뉴 */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24
      }}>
        {[
          { key: 'friends', label: '👥 친구 목록', color: '#10b981' },
          { key: 'search', label: '🔍 친구 찾기', color: '#3b82f6' },
          { key: 'requests', label: '📨 친구 요청', color: '#f59e0b' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              background: activeTab === tab.key ? tab.color : '#f8fafc',
              color: activeTab === tab.key ? '#fff' : tab.color,
              border: `2px solid ${tab.color}`,
              borderRadius: 12,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        minHeight: 400
      }}>
        {/* 친구 목록 */}
        {activeTab === 'friends' && (
          <div style={{ padding: 20 }}>
            <h3 style={{
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 16,
              color: '#1e293b'
            }}>
              💬 채팅 가능한 친구들
            </h3>
            
            {friends.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: '#64748b',
                padding: 40
              }}>
                아직 친구가 없습니다.<br/>
                친구를 추가해서 채팅을 시작해보세요!
              </div>
            ) : (
              friends.map(friend => (
                <div
                  key={friend.id}
                  onClick={() => setSelectedFriend(friend)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 16,
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    marginBottom: 8
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 18,
                    fontWeight: 700
                  }}>
                    {(friend.name || friend.nickname || '?')[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 16,
                      color: '#1e293b',
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      {friend.name || friend.nickname || '알 수 없음'}
                      {friend.unreadCount > 0 && (
                        <span style={{
                          background: '#ef4444',
                          color: '#fff',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {friend.unreadCount}
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: '#64748b'
                    }}>
                      {friend.tier || '루키'} • MMR {friend.mmr || 1000}
                    </div>
                    {friend.lastMessage && (
                      <div style={{
                        fontSize: 12,
                        color: friend.unreadCount > 0 ? '#1e293b' : '#94a3b8',
                        marginTop: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: friend.unreadCount > 0 ? 600 : 400
                      }}>
                        {friend.lastMessage}
                      </div>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4
                  }}>
                    {friend.lastMessageTime && (
                      <div style={{
                        fontSize: 11,
                        color: '#94a3b8'
                      }}>
                        {formatTime(friend.lastMessageTime)}
                      </div>
                    )}
                    {friend.unreadCount > 0 && (
                      <div style={{
                        fontSize: 10,
                        color: '#ef4444',
                        fontWeight: 600
                      }}>
                        읽지 않음
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* 친구 찾기 */}
        {activeTab === 'search' && (
          <div style={{ padding: 20 }}>
            <h3 style={{
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 16,
              color: '#1e293b'
            }}>
              🔍 사용자 검색
            </h3>
            
            <div style={{
              display: 'flex',
              gap: 12,
              marginBottom: 20
            }}>
              <input
                type="text"
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                placeholder="사용자 이름을 입력하세요"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none'
                }}
                onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
              />
              <button
                onClick={searchUsers}
                disabled={loading}
                style={{
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? '검색 중...' : '검색'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div>
                {searchResults.map(user => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 16,
                      borderRadius: 12,
                      background: '#f8fafc',
                      marginBottom: 8
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 16,
                        fontWeight: 700
                      }}>
                        {(user.name || user.nickname || '?')[0]}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#1e293b'
                        }}>
                          {user.name || user.nickname || '알 수 없음'}
                        </div>
                        <div style={{
                          fontSize: 12,
                          color: '#64748b'
                        }}>
                          {user.tier || '루키'} • MMR {user.mmr || 1000}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => sendFriendRequest(user.id)}
                      style={{
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      친구 요청
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 친구 요청 */}
        {activeTab === 'requests' && (
          <div style={{ padding: 20 }}>
            {/* 받은 요청 */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{
                fontSize: 16,
                fontWeight: 700,
                marginBottom: 16,
                color: '#1e293b'
              }}>
                📨 받은 친구 요청
              </h3>
              
              {friendRequests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: '#64748b',
                  padding: 20,
                  background: '#f8fafc',
                  borderRadius: 12
                }}>
                  받은 친구 요청이 없습니다.
                </div>
              ) : (
                friendRequests.map(request => (
                  <div
                    key={request.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 16,
                      borderRadius: 12,
                      background: '#f8fafc',
                      marginBottom: 8
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 16,
                        fontWeight: 700
                      }}>
                        {(request.fromUser.name || request.fromUser.nickname || '?')[0]}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#1e293b'
                        }}>
                          {request.fromUser.name || request.fromUser.nickname || '알 수 없음'}
                        </div>
                        <div style={{
                          fontSize: 12,
                          color: '#64748b'
                        }}>
                          {request.fromUser.tier || '루키'} • MMR {request.fromUser.mmr || 1000}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => acceptFriendRequest(request.id, request.from)}
                        style={{
                          background: '#10b981',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        수락
                      </button>
                      <button
                        onClick={() => rejectFriendRequest(request.id, request.from)}
                        style={{
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        거절
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 보낸 요청 */}
            <div>
              <h3 style={{
                fontSize: 16,
                fontWeight: 700,
                marginBottom: 16,
                color: '#1e293b'
              }}>
                📤 보낸 친구 요청
              </h3>
              
              {sentRequests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: '#64748b',
                  padding: 20,
                  background: '#f8fafc',
                  borderRadius: 12
                }}>
                  보낸 친구 요청이 없습니다.
                </div>
              ) : (
                sentRequests.map(request => (
                  <div
                    key={request.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 16,
                      borderRadius: 12,
                      background: '#f8fafc',
                      marginBottom: 8
                    }}
                  >
                    <div style={{
                      fontSize: 14,
                      color: '#64748b'
                    }}>
                      사용자 ID: {request.to}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: '#f59e0b',
                      fontWeight: 600
                    }}>
                      대기 중...
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DirectMessage;